/**
 * Analytics tracking configuration
 * Handles user behavior tracking and performance monitoring
 */

import { logger } from './logger';
import { siteConfig } from '@/constant/config';
import { visitorService, type VisitorData } from '@/services/api/visitors';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    mixpanel?: {
      track: (event: string, properties?: Record<string, unknown>) => void;
    };
  }
}

export interface AnalyticsEvent {
  name: string;
  properties?: Record<string, unknown>;
}

interface GeoData {
  city?: string;
  region?: string;
  country?: string;
  country_code?: string;
  country_calling_code?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;
  postal_code?: string;
  language?: string;
  currency?: string;
  org?: string;
  isp?: string;
}

interface EngagementMetrics {
  timeOnPage: number;
  scrollDepth: number;
  lastInteraction: number;
  interactionCount: number;
  focusTime: number;
}

class Analytics {
  private static instance: Analytics;
  private initialized: boolean = false;
  private userId: string | null = null;
  private userContext: {
    userId?: string;
    userEmail?: string;
    userName?: string;
    userRole?: string;
  } | null = null;
  private geoData: GeoData | null = null;
  private sessionStartTime: number = Date.now();
  private lastActivityTime: number = Date.now();
  private engagementMetrics: EngagementMetrics = {
    timeOnPage: 0,
    scrollDepth: 0,
    lastInteraction: 0,
    interactionCount: 0,
    focusTime: 0,
  };
  private pageLoadTime: number | null = null;
  private isPageFocused: boolean = true;
  private maxScrollDepth: number = 0;
  private visitorId: string | null = null;
  private hasTrackedVisitor: boolean = false;
  private currentVisitId: string | null = null;
  private userJourney: Array<{
    timestamp: number;
    event: string;
    page: string;
    data?: Record<string, unknown>;
  }> = [];

  // Journey batching configuration
  private journeyEventQueue: Array<{
    timestamp: number;
    event: string;
    page: string;
    data?: Record<string, unknown>;
  }> = [];
  private batchTimeout: NodeJS.Timeout | null = null;
  private readonly BATCH_SIZE = 8; // Send after 8 events
  private readonly BATCH_TIMEOUT = 30000; // Send after 30 seconds

  // Geo data caching constants
  private static readonly GEO_CACHE_KEY = 'geo_data_cache';
  private static readonly GEO_CACHE_DURATION = 8 * 60 * 60 * 1000; // 8 hours in milliseconds

  private constructor() {
    if (typeof window !== 'undefined') {
      this.setupEngagementTracking();
    }
  }

  private setupEngagementTracking(): void {
    // Track page focus/blur
    window.addEventListener('focus', () => {
      this.isPageFocused = true;
      this.trackUserEngagement('focus');
    });

    window.addEventListener('blur', () => {
      this.isPageFocused = false;
      this.trackUserEngagement('blur');
    });

    // Track scroll depth
    window.addEventListener(
      'scroll',
      this.debounce(() => {
        const scrollDepth = this.calculateScrollDepth();
        if (scrollDepth > this.maxScrollDepth) {
          this.maxScrollDepth = scrollDepth;
          this.trackUserEngagement('scroll_depth', { depth: scrollDepth });
        }
      }, 500)
    );

    // Track user interactions
    window.addEventListener('click', () => this.trackInteraction('click'));
    window.addEventListener('keypress', () =>
      this.trackInteraction('keypress')
    );
    window.addEventListener(
      'mousemove',
      this.debounce(() => this.trackInteraction('mousemove'), 1000)
    );

    // Track page load performance
    if (window.performance) {
      window.addEventListener('load', () => {
        const perfData = window.performance.timing;
        const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
        this.pageLoadTime = pageLoadTime;
        this.trackPerformanceMetrics();
      });
    }

    // Track before user leaves
    window.addEventListener('beforeunload', () => {
      this.trackSessionEnd();
    });
  }

  private debounce(
    func: (...args: unknown[]) => void,
    wait: number
  ): (...args: unknown[]) => void {
    let timeout: NodeJS.Timeout;
    return (...args: unknown[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  private calculateScrollDepth(): number {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return Math.round(((scrollTop + windowHeight) / documentHeight) * 100);
  }

  private trackInteraction(type: 'click' | 'keypress' | 'mousemove'): void {
    const now = Date.now();
    this.engagementMetrics.lastInteraction = now;
    this.engagementMetrics.interactionCount++;
    this.lastActivityTime = now;
    this.trackEvent({ name: 'user_interaction', properties: { type } });
  }

  private trackUserEngagement(
    action: string,
    details?: Record<string, unknown>
  ): void {
    this.trackEvent({
      name: `user_engagement_${action}`,
      properties: {
        ...details,
        time_since_last_activity: Date.now() - this.lastActivityTime,
        total_engagement_time: this.calculateEngagementTime(),
        interaction_count: this.engagementMetrics.interactionCount,
      },
    });
  }

  private calculateEngagementTime(): number {
    return this.isPageFocused ? Date.now() - this.sessionStartTime : 0;
  }

  private trackPerformanceMetrics(): void {
    if (!this.pageLoadTime) return;

    const metrics = {
      page_load_time: this.pageLoadTime,
      time_to_first_byte:
        window.performance.timing.responseStart -
        window.performance.timing.navigationStart,
      dom_load_time:
        window.performance.timing.domContentLoadedEventEnd -
        window.performance.timing.navigationStart,
      first_paint:
        window.performance.getEntriesByType('paint')[0]?.startTime || 0,
      first_contentful_paint:
        window.performance.getEntriesByType('paint')[1]?.startTime || 0,
    };

    this.trackEvent({
      name: 'performance_metrics',
      properties: metrics,
    });
  }

  private trackSessionEnd(): void {
    this.trackEvent({
      name: 'session_end',
      properties: {
        session_duration: Date.now() - this.sessionStartTime,
        max_scroll_depth: this.maxScrollDepth,
        total_interactions: this.engagementMetrics.interactionCount,
        engagement_time: this.calculateEngagementTime(),
      },
    });
  }

  static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  setUserId(id: string | null) {
    this.userId = id;
    if (window.gtag && id) {
      window.gtag('set', { user_id: id });
    }
  }

  async updateVisitorWithUserContext(): Promise<void> {
    if (!this.visitorId || !this.initialized) return;

    try {
      // Simply update session with user context - no complex journey tracking
      await visitorService.updateVisitorSession(this.visitorId, {
        userId: this.userContext?.userId,
        userEmail: this.userContext?.userEmail,
        userName: this.userContext?.userName,
        userRole: this.userContext?.userRole,
        userContextSetAt: Date.now(),
      });

      logger.debug('Visitor data updated with user context', {
        visitorId: this.visitorId,
        userId: this.userContext?.userId,
      });
    } catch (error) {
      logger.debug('Failed to update visitor with user context', { error });
    }
  }

  setUserContext(context: {
    userId?: string;
    userEmail?: string;
    userName?: string;
    userRole?: string;
  }) {
    const wasAnonymous = !this.userContext?.userId;
    this.userContext = context;

    // Only update visitor data when user context changes, no journey events
    if (this.initialized && wasAnonymous && context.userId) {
      this.updateVisitorWithUserContext();
    }
  }

  /**
   * Track user journey events with batching for efficiency
   */
  private trackJourneyEvent(
    event: string,
    data?: Record<string, unknown>
  ): void {
    const journeyEvent = {
      timestamp: Date.now(),
      event,
      page: typeof window !== 'undefined' ? window.location.pathname : '',
      data,
    };

    // Add to in-memory journey
    this.userJourney.push(journeyEvent);

    // Keep only last 50 events to prevent memory issues
    if (this.userJourney.length > 50) {
      this.userJourney = this.userJourney.slice(-50);
    }

    // Store journey in localStorage for persistence across page loads
    try {
      const storedJourney = JSON.parse(
        localStorage.getItem('user_journey') || '[]'
      );
      storedJourney.push(journeyEvent);

      // Keep only last 100 events in storage
      const trimmedJourney = storedJourney.slice(-100);
      localStorage.setItem('user_journey', JSON.stringify(trimmedJourney));
    } catch (error) {
      logger.debug('Failed to store journey event', { error });
    }

    // Add to batch queue for backend sending
    if (this.visitorId && this.initialized) {
      this.addToBatchQueue(journeyEvent);
    }
  }

  /**
   * Add event to batch queue and manage batching logic
   */
  private addToBatchQueue(journeyEvent: {
    timestamp: number;
    event: string;
    page: string;
    data?: Record<string, unknown>;
  }): void {
    this.journeyEventQueue.push(journeyEvent);

    // Send immediately if batch size reached
    if (this.journeyEventQueue.length >= this.BATCH_SIZE) {
      this.flushBatchQueue();
      return;
    }

    // Set timeout to send batch if not already set
    if (!this.batchTimeout) {
      this.batchTimeout = setTimeout(() => {
        this.flushBatchQueue();
      }, this.BATCH_TIMEOUT);
    }
  }

  /**
   * Send batched journey events to backend
   */
  private async flushBatchQueue(): Promise<void> {
    if (!this.visitorId || this.journeyEventQueue.length === 0) return;

    // Clear timeout
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    // Get events to send and clear queue
    const eventsToSend = [...this.journeyEventQueue];
    this.journeyEventQueue = [];

    try {
      await visitorService.trackJourneyEventsBatch(
        this.visitorId,
        eventsToSend
      );
      logger.debug('Journey events batch sent to backend', {
        visitorId: this.visitorId,
        eventCount: eventsToSend.length,
        events: eventsToSend.map((e) => e.event),
      });
    } catch (error) {
      logger.debug('Failed to send journey events batch to backend', { error });

      // Re-add failed events to queue for retry (but limit to prevent infinite growth)
      if (this.journeyEventQueue.length < this.BATCH_SIZE) {
        this.journeyEventQueue.unshift(...eventsToSend.slice(-this.BATCH_SIZE));
      }
    }
  }

  /**
   * Force flush any pending journey events (useful for page unload)
   */
  private async forceFlushJourneyEvents(): Promise<void> {
    if (this.journeyEventQueue.length > 0) {
      await this.flushBatchQueue();
    }
  }

  async init(): Promise<void> {
    if (this.initialized) return;

    // Initialize analytics services
    if (siteConfig.analytics.googleAnalyticsId) {
      // GA4 is now handled by @next/third-parties/google
      logger.debug('Google Analytics initialized');

      // Set default GA4 parameters
      if (window.gtag) {
        window.gtag('set', {
          allow_google_signals: true,
          allow_ad_personalization_signals: true,
        });
      }
    }

    if (process.env.NEXT_PUBLIC_MIXPANEL_TOKEN) {
      // Initialize Mixpanel
    }

    // Try to get geo data with caching
    this.geoData = await this.getCachedGeoData();

    // Load existing journey from storage
    try {
      const storedJourney = JSON.parse(
        localStorage.getItem('user_journey') || '[]'
      );
      this.userJourney = storedJourney.slice(-50); // Keep last 50 events in memory
    } catch (error) {
      logger.debug('Failed to load stored journey', { error });
    }

    // Track session start as simple journey event
    this.trackJourneyEvent('session_start', {
      sessionStartTime: this.sessionStartTime,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      entryPage: typeof window !== 'undefined' ? window.location.pathname : '',
    });

    // Track every visit (not just first-time) - ONLY ONCE
    await this.trackVisit();

    // Set up page unload handler to flush pending events
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        // Use sendBeacon for reliable delivery on page unload
        if (this.journeyEventQueue.length > 0 && this.visitorId) {
          const payload = JSON.stringify({
            visitorId: this.visitorId,
            events: this.journeyEventQueue,
          });

          try {
            navigator.sendBeacon(
              `${process.env.NEXT_PUBLIC_API_URL}/visitors/journey/batch`,
              payload
            );
          } catch (error) {
            logger.debug('Failed to send beacon on unload', { error });
          }
        }
      });
    }

    this.initialized = true;
  }

  /**
   * Get geo data with caching to reduce API calls
   */
  private async getCachedGeoData(): Promise<GeoData | null> {
    try {
      // Check if we have cached geo data
      const cachedData = localStorage.getItem(Analytics.GEO_CACHE_KEY);

      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);
        const now = Date.now();

        // Check if cached data is still valid (within 6 hours)
        if (now - timestamp < Analytics.GEO_CACHE_DURATION) {
          logger.debug('Using cached geo data', {
            cachedAt: new Date(timestamp).toISOString(),
            expiresAt: new Date(
              timestamp + Analytics.GEO_CACHE_DURATION
            ).toISOString(),
          });
          return data;
        } else {
          logger.debug('Cached geo data expired, fetching fresh data');
        }
      }

      // Fetch fresh geo data
      logger.debug('Fetching fresh geo data from ipapi.co');
      const response = await fetch('https://ipapi.co/json/');
      const geoData = await response.json();

      // Cache the fresh data with timestamp
      const cacheEntry = {
        data: geoData,
        timestamp: Date.now(),
      };
      localStorage.setItem(Analytics.GEO_CACHE_KEY, JSON.stringify(cacheEntry));

      logger.debug('Geo data cached successfully', {
        cachedAt: new Date().toISOString(),
        expiresAt: new Date(
          Date.now() + Analytics.GEO_CACHE_DURATION
        ).toISOString(),
      });

      return geoData;
    } catch (error) {
      logger.debug('Failed to fetch geo data', { error });

      // Try to return stale cached data if available
      const cachedData = localStorage.getItem(Analytics.GEO_CACHE_KEY);
      if (cachedData) {
        const { data } = JSON.parse(cachedData);
        logger.debug('Using stale cached geo data due to fetch error');
        return data;
      }

      return null;
    }
  }

  private async trackVisit(): Promise<void> {
    try {
      // Get or create visitor ID
      let visitorId = localStorage.getItem('visitor_id');
      if (!visitorId) {
        visitorId = this.generateVisitorId();
        localStorage.setItem('visitor_id', visitorId);
      }
      this.visitorId = visitorId;

      // Track this visit
      const visitorData = await this.collectComprehensiveVisitorData();

      try {
        const response = await visitorService.trackVisitor(visitorData);

        if (response.success) {
          this.currentVisitId = response.visitId || null;

          // Update visit count
          const visitCount =
            parseInt(localStorage.getItem('visit_count') || '0') + 1;
          localStorage.setItem('visit_count', visitCount.toString());
          localStorage.setItem('last_visit_date', new Date().toISOString());

          logger.debug('Visit tracked successfully', {
            visitorId: response.visitorId,
            visitId: response.visitId,
            visitNumber: visitCount,
          });

          // Set up periodic session updates
          this.setupSessionTracking();
        }
      } catch (error) {
        logger.error('Failed to track visit', error as Error);
      }
    } catch (error) {
      logger.error('Error in trackVisit', error as Error);
    }
  }

  private setupSessionTracking(): void {
    // Update session data every 30 seconds
    setInterval(() => {
      if (this.visitorId && this.currentVisitId) {
        this.updateSessionData();
      }
    }, 30000);

    // Update on page unload
    window.addEventListener('beforeunload', () => {
      if (this.visitorId && this.currentVisitId) {
        this.updateSessionData();
      }
    });
  }

  private async updateSessionData(): Promise<void> {
    if (!this.visitorId) return;

    try {
      await visitorService.updateVisitorSession(this.visitorId, {
        timeOnPage: Date.now() - this.sessionStartTime,
        scrollDepth: this.maxScrollDepth,
        interactionCount: this.engagementMetrics.interactionCount,
      });
    } catch (error) {
      logger.debug('Failed to update session data', { error });
    }
  }

  private async collectComprehensiveVisitorData(): Promise<VisitorData> {
    // Generate unique visitor ID if needed
    const visitorId = this.visitorId || this.generateVisitorId();
    const sessionId = this.getSessionId();

    // Get visit count
    const visitCount = parseInt(localStorage.getItem('visit_count') || '0') + 1;
    const lastVisitDate = localStorage.getItem('last_visit_date');
    const firstVisitDate =
      localStorage.getItem('first_visit_date') || new Date().toISOString();

    if (!localStorage.getItem('first_visit_date')) {
      localStorage.setItem('first_visit_date', firstVisitDate);
    }

    // Get GA Client ID if available
    let gaClientId: string | undefined;
    let gaSessionId: string | undefined;
    if (window.gtag) {
      try {
        window.gtag(
          'get',
          siteConfig.analytics.googleAnalyticsId,
          'client_id',
          (clientId: string) => {
            gaClientId = clientId;
          }
        );
        window.gtag(
          'get',
          siteConfig.analytics.googleAnalyticsId,
          'session_id',
          (sessionId: string) => {
            gaSessionId = sessionId;
          }
        );
      } catch (error) {
        logger.debug('Failed to get GA IDs', { error });
      }
    }

    // Get detailed browser and device info
    const browserInfo = this.getDetailedBrowserInfo();
    const deviceCapabilities = this.getDeviceCapabilities();
    const performanceMetrics = this.getPerformanceMetrics();

    return {
      visitorId,
      sessionId,
      timestamp: new Date().toISOString(),
      visitNumber: visitCount,

      // User context (if logged in)
      userId: this.userContext?.userId,
      userEmail: this.userContext?.userEmail,
      userName: this.userContext?.userName,
      userRole: this.userContext?.userRole,

      // Location data from geo API
      city: this.geoData?.city,
      region: this.geoData?.region,
      country: this.geoData?.country,
      countryCode: this.geoData?.country_code,
      callingCode: this.geoData?.country_calling_code,
      timezone: this.geoData?.timezone,
      latitude: this.geoData?.latitude,
      longitude: this.geoData?.longitude,
      postalCode: this.geoData?.postal_code,
      isp: this.geoData?.isp,
      organization: this.geoData?.org,

      // Enhanced Device & Browser info
      userAgent: navigator.userAgent,
      deviceType: this.getDeviceType(),
      browser: browserInfo.browser,
      browserVersion: browserInfo.version,
      operatingSystem: browserInfo.os,
      osVersion: browserInfo.osVersion,
      screenResolution: `${screen.width}x${screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      colorDepth: screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
      touchSupport: 'ontouchstart' in window,
      cookieEnabled: navigator.cookieEnabled,
      javaEnabled: navigator.javaEnabled?.() || false,

      // Network info
      connectionType: this.getConnectionInfo().type,
      connectionSpeed: this.getConnectionInfo().effectiveType,
      connectionDownlink: this.getConnectionInfo().downlink,
      connectionRtt: this.getConnectionInfo().rtt,
      onlineStatus: navigator.onLine,

      // Page context
      pageUrl: window.location.href,
      pagePath: window.location.pathname,
      pageTitle: document.title,
      referrer: document.referrer || undefined,
      referrerDomain: document.referrer
        ? new URL(document.referrer).hostname
        : undefined,
      language: navigator.language,
      languages: navigator.languages
        ? Array.from(navigator.languages)
        : undefined,

      // UTM & Campaign data
      utmSource: this.getUtmParam('utm_source') || undefined,
      utmMedium: this.getUtmParam('utm_medium') || undefined,
      utmCampaign: this.getUtmParam('utm_campaign') || undefined,
      utmTerm: this.getUtmParam('utm_term') || undefined,
      utmContent: this.getUtmParam('utm_content') || undefined,

      // Additional tracking IDs
      gaClientId,
      gaSessionId,
      fbClickId: this.getUtmParam('fbclid') || undefined,
      gclid: this.getUtmParam('gclid') || undefined,

      // Performance metrics
      ...performanceMetrics,

      // Session & Engagement data
      sessionStartTime: this.sessionStartTime,
      timeOnPage: 0, // Will be updated via session tracking
      scrollDepth: 0, // Will be updated via session tracking
      interactionCount: 0, // Will be updated via session tracking

      // Technical capabilities
      ...deviceCapabilities,

      // Additional browser capabilities
      doNotTrack: navigator.doNotTrack === '1',
      adBlocker: this.detectAdBlocker(),
      plugins: this.getPluginsList(),

      // Visit metadata
      isReturningVisitor: visitCount > 1,
      daysSinceFirstVisit: this.daysSinceFirstVisit(),
      totalVisits: visitCount,
      lastVisitDate,
    };
  }

  private getDeviceCapabilities(): {
    webglSupport?: boolean;
    webrtcSupport?: boolean;
    localStorageSupport?: boolean;
    sessionStorageSupport?: boolean;
    indexedDbSupport?: boolean;
  } {
    return {
      webglSupport: this.hasWebGLSupport(),
      webrtcSupport: this.hasWebRTCSupport(),
      localStorageSupport: this.hasLocalStorageSupport(),
      sessionStorageSupport: this.hasSessionStorageSupport(),
      indexedDbSupport: this.hasIndexedDBSupport(),
    };
  }

  private getPerformanceMetrics(): {
    pageLoadTime?: number;
    domLoadTime?: number;
    firstPaintTime?: number;
    firstContentfulPaintTime?: number;
  } {
    if (!window.performance) return {};

    const timing = window.performance.timing;
    const paintEntries = window.performance.getEntriesByType('paint');

    return {
      pageLoadTime: timing.loadEventEnd - timing.navigationStart,
      domLoadTime: timing.domContentLoadedEventEnd - timing.navigationStart,
      firstPaintTime: paintEntries.find((entry) => entry.name === 'first-paint')
        ?.startTime,
      firstContentfulPaintTime: paintEntries.find(
        (entry) => entry.name === 'first-contentful-paint'
      )?.startTime,
    };
  }

  private getDetailedBrowserInfo(): {
    browser: string;
    version?: string;
    os?: string;
    osVersion?: string;
  } {
    const ua = navigator.userAgent;

    // Browser detection with version
    let browser = 'unknown';
    let version: string | undefined;

    if (ua.includes('Chrome')) {
      browser = 'chrome';
      version = ua.match(/Chrome\/([0-9.]+)/)?.[1];
    } else if (ua.includes('Firefox')) {
      browser = 'firefox';
      version = ua.match(/Firefox\/([0-9.]+)/)?.[1];
    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
      browser = 'safari';
      version = ua.match(/Version\/([0-9.]+)/)?.[1];
    } else if (ua.includes('Edge')) {
      browser = 'edge';
      version = ua.match(/Edge\/([0-9.]+)/)?.[1];
    } else if (ua.includes('Opera')) {
      browser = 'opera';
      version = ua.match(/Opera\/([0-9.]+)/)?.[1];
    }

    // Enhanced OS detection with version
    let os = 'unknown';
    let osVersion: string | undefined;

    if (ua.includes('Windows NT')) {
      os = 'windows';
      osVersion = ua.match(/Windows NT ([0-9.]+)/)?.[1];
    } else if (ua.includes('Mac OS X')) {
      os = 'macos';
      osVersion = ua.match(/Mac OS X ([0-9_]+)/)?.[1]?.replace(/_/g, '.');
    } else if (ua.includes('Linux')) {
      os = 'linux';
    } else if (ua.includes('Android')) {
      os = 'android';
      osVersion = ua.match(/Android ([0-9.]+)/)?.[1];
    } else if (ua.includes('iPhone OS') || ua.includes('iOS')) {
      os = 'ios';
      osVersion = ua.match(/OS ([0-9_]+)/)?.[1]?.replace(/_/g, '.');
    }

    return { browser, version, os, osVersion };
  }

  // Helper methods for capability detection
  private hasWebGLSupport(): boolean {
    try {
      const canvas = document.createElement('canvas');
      return !!(
        canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
      );
    } catch {
      return false;
    }
  }

  private hasWebRTCSupport(): boolean {
    return !!(
      window.RTCPeerConnection ||
      window.webkitRTCPeerConnection ||
      window.mozRTCPeerConnection
    );
  }

  private hasLocalStorageSupport(): boolean {
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      return true;
    } catch {
      return false;
    }
  }

  private hasSessionStorageSupport(): boolean {
    try {
      sessionStorage.setItem('test', 'test');
      sessionStorage.removeItem('test');
      return true;
    } catch {
      return false;
    }
  }

  private hasIndexedDBSupport(): boolean {
    return !!(
      window.indexedDB ||
      window.webkitIndexedDB ||
      window.mozIndexedDB
    );
  }

  private detectAdBlocker(): boolean {
    // Simple ad blocker detection
    try {
      const testAd = document.createElement('div');
      testAd.innerHTML = '&nbsp;';
      testAd.className = 'adsbox';
      testAd.style.position = 'absolute';
      testAd.style.left = '-10000px';
      document.body.appendChild(testAd);
      const isBlocked = testAd.offsetHeight === 0;
      document.body.removeChild(testAd);
      return isBlocked;
    } catch {
      return false;
    }
  }

  private getPluginsList(): string[] {
    try {
      return Array.from(navigator.plugins).map((plugin) => plugin.name);
    } catch {
      return [];
    }
  }

  private generateVisitorId(): string {
    return (
      'visitor_' + Math.random().toString(36).substring(2) + '_' + Date.now()
    );
  }

  private getDeviceType(): 'mobile' | 'desktop' | 'tablet' {
    const ua = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(ua)) {
      return 'tablet';
    }
    if (
      /mobile|android|iphone|ipod|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(
        ua
      )
    ) {
      return 'mobile';
    }
    return 'desktop';
  }

  private getConnectionInfo(): {
    type?: string;
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  } {
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection;

    if (!connection) return {};

    return {
      type: connection.type,
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
    };
  }

  private getUserAgent() {
    if (typeof window === 'undefined') return {};

    const ua = window.navigator.userAgent;
    const mobile =
      /Mobile|Android|iP(hone|od|ad)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        ua
      );

    return {
      user_agent: ua,
      device_type: mobile ? 'mobile' : 'desktop',
      browser: this.getBrowserInfo(),
      screen_resolution:
        typeof window !== 'undefined'
          ? `${window.screen.width}x${window.screen.height}`
          : undefined,
      viewport_size:
        typeof window !== 'undefined'
          ? `${window.innerWidth}x${window.innerHeight}`
          : undefined,
      color_depth:
        typeof window !== 'undefined' ? window.screen.colorDepth : undefined,
    } as const;
  }

  private getBrowserInfo(): string {
    const ua = window.navigator.userAgent;
    const browsers = {
      chrome: /chrome|chromium|crios/i,
      safari: /safari/i,
      firefox: /firefox|fxios/i,
      edge: /edg/i,
      opera: /opera|opr/i,
    };

    for (const [browser, regex] of Object.entries(browsers)) {
      if (regex.test(ua)) return browser;
    }
    return 'other';
  }

  private enrichEventProperties(
    properties?: Record<string, unknown>
  ): Record<string, unknown> {
    const connection =
      typeof navigator !== 'undefined' && 'connection' in navigator
        ? (
            navigator as Navigator & {
              connection: {
                type?: string;
                effectiveType?: string;
                downlink?: number;
                rtt?: number;
              };
            }
          ).connection
        : null;

    return {
      ...properties,
      // User context
      user_id: this.userId,
      timestamp: new Date().toISOString(),

      // Environment
      environment: process.env.NODE_ENV,
      app_version: process.env.NEXT_PUBLIC_APP_VERSION,

      // Page context
      page_url:
        typeof window !== 'undefined' ? window.location.href : undefined,
      page_path:
        typeof window !== 'undefined' ? window.location.pathname : undefined,
      page_title: typeof document !== 'undefined' ? document.title : undefined,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,

      // Enhanced Geo context
      city: this.geoData?.city,
      region: this.geoData?.region,
      country: this.geoData?.country,
      timezone: this.geoData?.timezone,
      latitude: this.geoData?.latitude,
      longitude: this.geoData?.longitude,
      postal_code: this.geoData?.postal_code,
      language: this.geoData?.language || navigator.language,
      currency: this.geoData?.currency,

      // Enhanced Device & Browser info
      ...this.getUserAgent(),

      // Network context
      connection_type: connection?.type,
      connection_speed: connection?.effectiveType,
      connection_downlink: connection?.downlink,
      connection_rtt: connection?.rtt,
      online_status:
        typeof navigator !== 'undefined' ? navigator.onLine : undefined,

      // Enhanced Campaign context
      utm_source: this.getUtmParam('utm_source'),
      utm_medium: this.getUtmParam('utm_medium'),
      utm_campaign: this.getUtmParam('utm_campaign'),
      utm_term: this.getUtmParam('utm_term'),
      utm_content: this.getUtmParam('utm_content'),

      // Session context
      session_id: this.getSessionId(),
      session_start_time: this.sessionStartTime,
      time_on_page: Date.now() - this.sessionStartTime,
      engagement_time: this.calculateEngagementTime(),
      scroll_depth: this.maxScrollDepth,
      interaction_count: this.engagementMetrics.interactionCount,
      last_interaction: this.engagementMetrics.lastInteraction,

      // Performance context
      page_load_time: this.pageLoadTime,

      // Custom dimensions
      is_returning_user: this.isReturningUser(),
      days_since_first_visit: this.daysSinceFirstVisit(),
      user_segment: this.getUserSegment(),
    };
  }

  private isReturningUser(): boolean {
    const firstVisit = localStorage.getItem('first_visit');
    if (!firstVisit) {
      localStorage.setItem('first_visit', Date.now().toString());
      return false;
    }
    return true;
  }

  private daysSinceFirstVisit(): number {
    const firstVisit = localStorage.getItem('first_visit');
    if (!firstVisit) return 0;
    const days = (Date.now() - parseInt(firstVisit)) / (1000 * 60 * 60 * 24);
    return Math.floor(days);
  }

  private getUserSegment(): string {
    const visitCount = parseInt(localStorage.getItem('visit_count') || '0') + 1;
    localStorage.setItem('visit_count', visitCount.toString());

    if (visitCount === 1) return 'new';
    if (visitCount < 5) return 'occasional';
    if (visitCount < 10) return 'regular';
    return 'loyal';
  }

  private getUtmParam(param: string): string | null {
    if (typeof window === 'undefined') return null;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  private getSessionId(): string {
    if (typeof window === 'undefined') return '';
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = Math.random().toString(36).substring(2);
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }

  trackEvent(event: AnalyticsEvent): void {
    try {
      // Ensure analytics is initialized before tracking any event
      if (!this.initialized) {
        // Initialize analytics immediately and track the event after initialization
        this.init()
          .then(() => {
            this.trackEventInternal(event);
          })
          .catch((error) => {
            logger.error(
              'Failed to initialize analytics before tracking event',
              error as Error,
              { event }
            );
          });
        return;
      }

      this.trackEventInternal(event);
    } catch (error) {
      logger.error('Failed to track analytics event', error as Error, {
        event,
      });
    }
  }

  private trackEventInternal(event: AnalyticsEvent): void {
    const enrichedProperties = this.enrichEventProperties(event.properties);

    if (window.gtag) {
      window.gtag('event', event.name, enrichedProperties);
    }

    if (window.mixpanel) {
      window.mixpanel.track(event.name, enrichedProperties);
    }

    logger.debug('Analytics event tracked', {
      event: { ...event, enrichedProperties },
    });
  }

  trackPageView(path: string): void {
    // Ensure analytics is initialized before tracking page view
    if (!this.initialized) {
      this.init()
        .then(() => {
          this.trackPageViewInternal(path);
        })
        .catch((error) => {
          logger.error(
            'Failed to initialize analytics before tracking page view',
            error as Error,
            { path }
          );
        });
      return;
    }

    this.trackPageViewInternal(path);
  }

  private trackPageViewInternal(path: string): void {
    // Track page view as simple journey event (no API call)
    this.trackJourneyEvent('page_view', {
      path,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      timestamp: Date.now(),
    });

    // Track normal analytics event
    this.trackEvent({
      name: 'page_view',
      properties: {
        path,
        referrer:
          typeof document !== 'undefined' ? document.referrer : undefined,
        journey_length: this.userJourney.length,
      },
    });
  }

  trackError(error: Error, context?: Record<string, unknown>): void {
    this.trackEvent({
      name: 'error',
      properties: {
        message: error.message,
        stack: error.stack,
        ...context,
      },
    });
  }

  trackPerformance(metric: {
    name: string;
    value: number;
    label?: string;
  }): void {
    this.trackEvent({
      name: 'performance_metric',
      properties: metric,
    });
  }

  // Custom event tracking methods with simple journey tracking
  trackUserAction(action: string, details?: Record<string, unknown>): void {
    // Track as simple journey event (no API call)
    this.trackJourneyEvent('user_action', {
      action,
      ...details,
    });

    // Track normal analytics event
    this.trackEvent({
      name: `user_action_${action}`,
      properties: {
        ...details,
        journey_length: this.userJourney.length,
      },
    });
  }

  trackAuctionEvent(
    action: string,
    auctionDetails: Record<string, unknown>
  ): void {
    this.trackEvent({
      name: `auction_${action}`,
      properties: auctionDetails,
    });
  }

  trackBidEvent(bidDetails: Record<string, unknown>): void {
    this.trackEvent({
      name: 'bid_placed',
      properties: bidDetails,
    });
  }
}

export const analytics = Analytics.getInstance();
